import { forwardRef } from 'react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import type { LucideIcon } from 'lucide-react';

// Utility for combining Tailwind classes
function cn(...inputs: (string | undefined | null | false)[]) {
  return twMerge(clsx(inputs));
}

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost' | 'link';
  size?: 'sm' | 'md' | 'lg' | 'icon' | 'icon-sm';
  isLoading?: boolean;
  leftIcon?: LucideIcon;
  rightIcon?: LucideIcon;
  fullWidth?: boolean;
}

const variantStyles = {
  primary:
    'bg-amber-600 text-white hover:bg-amber-700 focus:ring-amber-500 shadow-sm',
  secondary:
    'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 focus:ring-gray-400 shadow-sm',
  danger:
    'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 shadow-sm',
  ghost:
    'text-gray-600 hover:bg-gray-100 hover:text-gray-900 focus:ring-gray-400',
  link:
    'text-amber-600 hover:text-amber-700 underline-offset-4 hover:underline focus:ring-amber-500 p-0',
};

const sizeStyles = {
  sm: 'text-xs px-2.5 py-1.5 gap-1.5 rounded-md',
  md: 'text-sm px-4 py-2 gap-2 rounded-lg',
  lg: 'text-base px-6 py-3 gap-2 rounded-lg',
  icon: 'p-2 rounded-lg',
  'icon-sm': 'p-1.5 rounded-md',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      isLoading = false,
      leftIcon: LeftIcon,
      rightIcon: RightIcon,
      fullWidth = false,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const isIconOnly = size === 'icon' || size === 'icon-sm';
    const iconSize = size === 'sm' || size === 'icon-sm' ? 'h-3.5 w-3.5' : size === 'lg' ? 'h-5 w-5' : 'h-4 w-4';

    return (
      <button
        ref={ref}
        disabled={disabled || isLoading}
        className={cn(
          'inline-flex items-center justify-center font-semibold transition-all duration-150',
          'focus:outline-none focus:ring-2 focus:ring-offset-2',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          variantStyles[variant],
          sizeStyles[size],
          fullWidth && 'w-full',
          className
        )}
        {...props}
      >
        {isLoading ? (
          <>
            <span
              className={cn(
                'animate-spin rounded-full border-2 border-current border-t-transparent',
                iconSize
              )}
            />
            {!isIconOnly && <span className="ml-2">Loading...</span>}
          </>
        ) : (
          <>
            {LeftIcon && <LeftIcon className={iconSize} />}
            {children}
            {RightIcon && <RightIcon className={iconSize} />}
          </>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

// Convenience components for common button patterns
interface IconButtonProps extends Omit<ButtonProps, 'leftIcon' | 'rightIcon' | 'children'> {
  icon: LucideIcon;
  'aria-label': string;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ icon: Icon, size = 'icon', ...props }, ref) => {
    return (
      <Button ref={ref} size={size} {...props}>
        <Icon className={size === 'icon-sm' ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
      </Button>
    );
  }
);

IconButton.displayName = 'IconButton';

// Button group for related actions
interface ButtonGroupProps {
  children: React.ReactNode;
  className?: string;
}

export function ButtonGroup({ children, className }: ButtonGroupProps) {
  return (
    <div
      className={cn(
        'inline-flex rounded-lg border border-gray-200 divide-x divide-gray-200 overflow-hidden',
        '[&>button]:rounded-none [&>button]:border-0 [&>button]:shadow-none',
        '[&>button:first-child]:rounded-l-lg [&>button:last-child]:rounded-r-lg',
        className
      )}
    >
      {children}
    </div>
  );
}
